// src/hooks/useData.js
import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import { getProductByCode } from "../constants/products";
import { getColorsByProductCode } from "../constants/colors";
import { FONTS } from "../constants/fonts";
import { getDefaultLineValues } from "../utils/lineDefaults"; // ← NEW
import { v4 as uuidv4 } from "uuid"; // ← RECOMMENDED: use real UUIDs

const DataContext = createContext();

export function DataProvider({ children }) {
  const [state, setState] = useState({
    selectedProduct: null,
    productCode: null,
    canvas: null,
    placeholderRef: null,
    lines: [],
    quantity: 1,
    totalPrice: 0,
    snackbar: { open: false, message: "", severity: "info" },
    mivaGlobals: null,
  });

  const [loading, setLoading] = useState(false);

  /* ────────────────────── SETTERS ────────────────────── */
  const setCanvas = (c) => setState((s) => ({ ...s, canvas: c }));
  const setPlaceholderRef = (r) =>
    setState((s) => ({ ...s, placeholderRef: r }));
  const setTotalPrice = (p) => setState((s) => ({ ...s, totalPrice: p }));
  const setSnackbar = (sn) => setState((s) => ({ ...s, snackbar: sn }));
  const setQuantity = (q) =>
    setState((s) => ({ ...s, quantity: Math.max(1, q) }));
  const setMivaGlobals = (g) => setState((s) => ({ ...s, mivaGlobals: g }));

  /* ────────────────────── PRODUCT ────────────────────── */
  const setSelectedProduct = (product) => {
    if (!product) return;

    setState((s) => ({
      ...s,
      selectedProduct: product,
      productCode: product.code,
      lines: [], // fresh start
    }));
  };

  /* ────────────────────── LINES ────────────────────── */
  const updateLine = useCallback((id, updates) => {
    setState((s) => ({
      ...s,
      lines: s.lines.map((l) => (l.id === id ? { ...l, ...updates } : l)),
    }));
  }, []);

  const addLine = useCallback(() => {
    if (state.lines.length >= 5) {
      setSnackbar({
        open: true,
        message: "Maximum 5 lines allowed",
        severity: "warning",
      });
      return;
    }

    const defaults = getDefaultLineValues(state.selectedProduct);

    const newLine = {
      id: uuidv4(), // ← real UUID, no collisions
      ...defaults,
      text: "", // ensure empty text
    };

    setState((s) => ({ ...s, lines: [...s.lines, newLine] }));
  }, [state.selectedProduct, state.lines.length, setSnackbar]);

  const removeLine = useCallback((id) => {
    setState((s) => ({
      ...s,
      lines: s.lines.filter((l) => l.id !== id),
    }));
  }, []);

  const setCanvasObj = useCallback((lineId, canvasObj) => {
    setState((s) => ({
      ...s,
      lines: s.lines.map((l) => (l.id === lineId ? { ...l, canvasObj } : l)),
    }));
  }, []);

  // ONE-TIME MIGRATION: Fix any legacy lines missing defaults
  useEffect(() => {
    if (!state.selectedProduct || state.lines.length === 0) return;

    const defaults = getDefaultLineValues(state.selectedProduct);

    setState((s) => ({
      ...s,
      lines: s.lines.map((line) => {
        const hasValidProps =
          line.size &&
          line.sizeName &&
          line.color &&
          line.colorName &&
          line.font;

        if (hasValidProps) return line; // already good

        return {
          ...defaults,
          ...line, // preserve id, text, canvasObj, price
          text: line.text || "",
          canvasObj: line.canvasObj || null,
        };
      }),
    }));
  }, [state.selectedProduct]);

  // AUTO-ADD first line when product changes and lines are empty
  useEffect(() => {
    if (state.selectedProduct && state.lines.length === 0) {
      addLine();
    }
  }, [state.selectedProduct, state.lines.length, addLine]);

  const reset = () =>
    setState({
      selectedProduct: null,
      productCode: null,
      canvas: null,
      placeholderRef: null,
      lines: [],
      quantity: 1,
      totalPrice: 0,
      snackbar: { open: false, message: "", severity: "info" },
      mivaGlobals: null,
    });

  /* ────────────────────── BASKET ITEM (derived) ────────────────────── */
  const basketItem = useMemo(() => {
    if (!state.selectedProduct) return { productCode: "", lines: [] };

    return {
      productCode: state.selectedProduct.code,
      lines: state.lines
        .filter((line) => line.text?.trim())
        .map((line) => ({
          id: line.id,
          phrase: line.text?.trim() || "",
          font: line.font || "",
          color: line.color || "",
          size: line.size || "",
          sizeName: line.sizeName || "",
          colorName: line.colorName || "",
          price: Number(line.price) || 0,
        })),
    };
  }, [state.selectedProduct, state.lines]);

  /* ────────────────────── PROVIDER VALUE ────────────────────── */
  const value = {
    ...state,
    basketItem,
    setCanvas,
    setPlaceholderRef,
    setTotalPrice,
    setSnackbar,
    setQuantity,
    setMivaGlobals,
    setSelectedProduct,
    updateLine,
    addLine,
    removeLine,
    reset,
    setCanvasObj,
    loading,
    setLoading,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export const useData = () => {
  const ctx = useContext(DataContext);
  if (!ctx) throw new Error("useData must be used inside DataProvider");
  return ctx;
};
